import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import qs from 'query-string'
import { CAMPAIGN_STATUS_ENUM, deleteCampaign, syncCampaignType } from 'state/campaigns'
import { selectCampaign } from 'state'
import { duplicateCampaign } from 'state/campaigns'
import { useNewTranslation } from 'hooks'
import { selectIsInternalUser } from 'state/profile'
import { CampaignManagement } from 'components/Shared/Permission/enums'
import Restricted from 'components/Shared/Permission/Restricted'
import Divider from 'components/Shared/Divider'
import styled from 'particles/themes/styled'
import MenuItem from 'particles/MenuItem'
import Dropdown from 'particles/Dropdown'
import { useCheckEditCampaignPermission } from 'hooks/permissions'
import Modal, { ModalProps } from 'particles/Modal'
import Text from 'particles/Text'
import { getCampaignStatusColor, getCampaignStatusTitle } from 'components/utils'
import CampaignActions from 'components/Campaign/CampaignActions'

const EditorTitleContainer = styled.div`
    display: flex;
    align-items: center;
    margin-top: 12px;
    overflow-x: auto;
    overflow-y: hidden;
    span:not(:first-child) {
        white-space: nowrap;
    }
    .title-text {
        white-space: nowrap;
    }
    .divider {
        margin: 0 12px;
    }
    .duplicate-action {
        width: 100%;
        background-color: transparent;
    }
`

const EditorTitle: React.FC = () => {
    const [modalState, setModalState] = useState<ModalProps | null>()
    const location = useLocation()
    const [t] = useNewTranslation()
    const dispatch = useDispatch()
    const { campaign: uuid } = qs.parse(location.search)
    const campaign = useSelector(selectCampaign(uuid && typeof uuid === 'string' ? uuid : ''))
    const isInternalUser = useSelector(selectIsInternalUser)
    const isAbleToUpdateCampaignType = useCheckEditCampaignPermission(campaign?.status)

    const CampaignTypeOptions = [
        {
            title: t('campaignEditor.campaignType.selfServe'),
            description: t('campaignEditor.campaignType.selfServeDescription'),
            icon: 'Handshake',
            value: true,
        },
        {
            title: t('campaignEditor.campaignType.managedService'),
            description: t('campaignEditor.campaignType.managedServiceDescription'),
            icon: 'HandCoins',
            value: false,
        },
    ]

    const handleDuplicateCampaign = () => {
        dispatch(
            duplicateCampaign({
                uuid: campaign.uuid,
            }),
        )
    }

    const handleDeleteCampaign = () => {
        setModalState({
            title: t('modalWindow.campaign.deleteCampaignHeader', { campaignName: campaign?.title }),
            subtext: t('modalWindow.campaign.deleteCampaignSubtext'),
            secondaryButtonProps: { label: t('button.noCancel') },
            primaryButtonProps: { label: t('button.delete') },
            size: 'm',
            onConfirm: () => {
                dispatch(
                    deleteCampaign({
                        uuid: campaign.uuid,
                    }),
                )
                setModalState(null)
            },
            onClose: () => {
                setModalState(null)
            },
        })
    }

    const handleChangeCampaignType = (value: boolean) => {
        setModalState({
            title: value
                ? t('modalWindow.campaignEditor.changeToSelfServeTitle')
                : t('modalWindow.campaignEditor.changeToManagedServiceWindowTitle'),
            subtext: value
                ? t('modalWindow.campaignEditor.changeToSelfServeSubTitle')
                : t('modalWindow.campaignEditor.changeToManagedServiceSubtitle'),
            secondaryButtonProps: { label: t('button.noCancel') },
            primaryButtonProps: { label: t('button.yesChange') },
            size: 'm',
            onConfirm: () => {
                if (campaign?.uuid) {
                    dispatch(
                        syncCampaignType({
                            uuid: campaign.uuid,
                            self_serve: value,
                        }),
                    )
                }
                setModalState(null)
            },
            onClose: () => {
                setModalState(null)
            },
        })
    }

    const titleType = useMemo(() => {
        if (campaign) {
            return { type: getCampaignStatusTitle(campaign.status, t), title: campaign.title }
        } else {
            return { type: CAMPAIGN_STATUS_ENUM[0], title: '' }
        }
    }, [campaign])

    const statusColor = getCampaignStatusColor(campaign?.status)

    return (
        <>
            <EditorTitleContainer>
                {Boolean(isInternalUser && campaign?.status !== undefined) && (
                    <>
                        <Dropdown
                            width={160}
                            size="xs"
                            icon={campaign?.self_serve ? 'Handshake' : 'HandCoins'}
                            value={campaign?.self_serve}
                            renderValue={() => (
                                <Text
                                    scale="bodyS400"
                                    style={{
                                        width: '100%',
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {campaign?.self_serve
                                        ? t('campaignEditor.campaignType.selfServe')
                                        : t('campaignEditor.campaignType.managedService')}
                                </Text>
                            )}
                            isReadOnly={!isAbleToUpdateCampaignType}
                        >
                            {CampaignTypeOptions.map((option: any, index: number) => (
                                <MenuItem.Text
                                    key={`campaign-type-option-${index}`}
                                    text={option.title}
                                    description={option.description}
                                    icon={option.icon}
                                    value={option.value}
                                    isRichContentItem
                                    onClick={() => handleChangeCampaignType(option.value)}
                                    isDisabled={campaign?.self_serve === option.value}
                                />
                            ))}
                        </Dropdown>
                        <Divider width="2px" height="16px" className="divider" />
                    </>
                )}
                <Text scale="bodyL500" color={statusColor} className="title-text">
                    {titleType.type}
                </Text>
                <Divider width="2px" height="16px" className="divider" />
                <Text scale="bodyL500" color="primary" className="title-text">
                    {titleType.title}
                </Text>
                <Restricted
                    permissions={[
                        CampaignManagement.CreateAndEditBriefsForInternal,
                        CampaignManagement.CreateAndEditBriefsForExternal,
                    ]}
                >
                    <Divider width="2px" height="16px" className="divider" />
                    <span>
                        <CampaignActions
                            campaign={campaign}
                            onDuplicate={handleDuplicateCampaign}
                            onDelete={handleDeleteCampaign}
                        />
                    </span>
                </Restricted>
            </EditorTitleContainer>
            {modalState && <Modal {...modalState} />}
        </>
    )
}

export default EditorTitle
